/**
 * batch-65 apk-rapport (FO-478) - pure regels achter het APK-rapport.
 *
 * Geen I/O en geen `new Date()` buiten de meegegeven peildatum, zodat de tests
 * elk grensgeval kunnen vastzetten. Gedeeld door de API (registratie, RDW-
 * afstemming) en het upload-formulier (directe waarschuwingen tijdens typen).
 *
 *  - `apkRapportWaarschuwingen`  spec §6: vervaldatum buiten bereik, tweede
 *                                rapport binnen 30 dagen, uitgefaseerd voertuig
 *                                (alle drie alleen waarschuwen) en een afwijkend
 *                                kenteken (blokkerend tot bevestigd).
 *  - `bepaalApkActualiteit`      is dit rapport de actuele keuring (nieuwste
 *                                bekende en recent)? Alleen dan raakt het de
 *                                lopende APK-status (afspraak, signalen, postvak).
 *  - `bepaalApkVoorstel`         wat een rapport met de APK-vervaldatum doet:
 *                                een VOORSTEL (nooit stil op het voertuig), of
 *                                niets omdat de datum al actueel/historisch is.
 *  - `beoordeelApkRdwAfstemming` de RDW-sync bevestigt een open voorstel bij een
 *                                gelijke datum en signaleert een afwijking (na
 *                                een paar dagen verwerkingsruimte bij RDW).
 */

import type {
  ApkKeuringMomentDTO,
  ApkKeuringUitslag,
  ApkRapportWaarschuwing,
  ApkVoorstelUitkomst,
  GarageApkVoorstelStatus,
  VoertuigType,
} from '@streep/types'
import { formatKenteken, normalizeKenteken } from './voertuig-utils'
import { plusDagenYmd, plusMaandenYmd } from './telefonie'
import { dagenTussenNlDagen } from './certificaat-status'

/** Venster waarbinnen een tweede rapport een dubbel-waarschuwing geeft (§6.2). */
export const APK_DUBBEL_VENSTER_DAGEN = 30

/**
 * Hoe ver de vervaldatum maximaal na de keuring mag liggen voor hij verdacht is
 * (§6.1): 2 jaar voor een personenauto, 1 jaar voor de rest - plus 2 maanden,
 * omdat je tot 2 maanden vóór de vervaldatum mag keuren zonder de oude datum te
 * verliezen (de nieuwe datum = oude datum + termijn).
 */
export const APK_VERVROEGD_KEUREN_MAANDEN = 2

/** RDW-verwerkingsruimte na de keuring voordat een afwijking gesignaleerd wordt. */
export const APK_RDW_VERWERKING_DAGEN = 3

/**
 * Venster (dagen) waarbinnen twee APK-regels als dezelfde keuring tellen: een
 * keuring meer dan dit aantal dagen na die van het rapport is een NIEUWERE keuring.
 */
export const APK_ZELFDE_KEURING_DAGEN = 3

/**
 * Hoe oud een keuring maximaal is om nog als de actuele keuring te tellen (en dus
 * de lopende APK-status te raken). Een ouder rapport is historie.
 */
export const APK_ACTUEEL_DAGEN = 90

/** dd-mm-jjjj van een yyyy-mm-dd (weergave in meldingen). */
export function apkNlDatum(ymd: string): string {
  return `${ymd.slice(8, 10)}-${ymd.slice(5, 7)}-${ymd.slice(0, 4)}`
}

/** Termijn in jaren waarbinnen de vervaldatum na de keuring hoort te liggen. */
export function apkTermijnJaren(type: VoertuigType | null | undefined): number {
  return type === 'PERSONENAUTO' ? 2 : 1
}

export interface ApkRapportWaarschuwingInput {
  keuringsdatum: string | null
  vervaldatum: string | null
  voertuig: { kenteken: string; type: VoertuigType | null; uitgefaseerd: boolean } | null
  /** Kenteken zoals het rapport het noemt (vrije vorm of genormaliseerd), of null. */
  kentekenRapport: string | null
  kentekenAfwijkingBevestigd: boolean
  /** Keuringsdatums (yyyy-mm-dd) van ANDERE rapporten van hetzelfde voertuig. */
  eerdereKeuringsdatums: readonly string[]
}

/** De waarschuwingen van spec §6 bij de huidige invoer (volgorde = UI-volgorde). */
export function apkRapportWaarschuwingen(
  input: ApkRapportWaarschuwingInput,
): ApkRapportWaarschuwing[] {
  const uit: ApkRapportWaarschuwing[] = []
  const { keuringsdatum, vervaldatum, voertuig } = input

  // §6.4 - kenteken wijkt af: blokkerend tot bevestigd.
  if (voertuig && input.kentekenRapport) {
    const rapport = normalizeKenteken(input.kentekenRapport)
    const eigen = normalizeKenteken(voertuig.kenteken)
    if (rapport.length > 0 && eigen.length > 0 && rapport !== eigen) {
      uit.push({
        code: 'KENTEKEN_AFWIJKING',
        melding:
          `Het rapport noemt kenteken ${formatKenteken(rapport)}, maar het gekozen voertuig is ` +
          `${formatKenteken(eigen)}. Kies het juiste voertuig, of bevestig dat dit rapport bij dit voertuig hoort.`,
        blokkerend: !input.kentekenAfwijkingBevestigd,
      })
    }
  }

  // §6.1 - vervaldatum buiten het verwachte bereik (alleen waarschuwen).
  if (keuringsdatum && vervaldatum) {
    if (vervaldatum < keuringsdatum) {
      uit.push({
        code: 'VERVALDATUM_VOOR_KEURING',
        melding: `De nieuwe vervaldatum (${apkNlDatum(vervaldatum)}) ligt vóór de keuringsdatum. Controleer de datum op het rapport.`,
        blokkerend: false,
      })
    } else {
      const jaren = apkTermijnJaren(voertuig?.type)
      const grens = plusMaandenYmd(keuringsdatum, jaren * 12 + APK_VERVROEGD_KEUREN_MAANDEN)
      if (vervaldatum > grens) {
        uit.push({
          code: 'VERVALDATUM_TE_VER',
          melding:
            `De nieuwe vervaldatum (${apkNlDatum(vervaldatum)}) ligt meer dan ${jaren === 1 ? '1 jaar' : `${jaren} jaar`} ` +
            'na de keuring. Dat is ongebruikelijk; controleer de datum op het rapport.',
          blokkerend: false,
        })
      }
    }
  }

  // §6.2 - tweede rapport binnen 30 dagen (herkeuring is legitiem: alleen waarschuwen).
  if (keuringsdatum) {
    const dichtbij = input.eerdereKeuringsdatums
      .filter((d) => Math.abs(dagenTussenNlDagen(d, keuringsdatum)) <= APK_DUBBEL_VENSTER_DAGEN)
      .sort()
    if (dichtbij.length > 0) {
      uit.push({
        code: 'DUBBEL_BINNEN_30_DAGEN',
        melding:
          `Er is al een APK-rapport van ${apkNlDatum(dichtbij[dichtbij.length - 1]!)} voor dit voertuig ` +
          `(binnen ${APK_DUBBEL_VENSTER_DAGEN} dagen). Gaat het om een herkeuring, dan kun je gewoon doorgaan.`,
        blokkerend: false,
      })
    }
  }

  // §6.3 - uitgefaseerd voertuig: koppelen mag, met melding.
  if (voertuig?.uitgefaseerd) {
    uit.push({
      code: 'VOERTUIG_UITGEFASEERD',
      melding:
        'Dit voertuig is niet meer in het wagenpark (verkocht of afgeschreven). Het rapport wordt wel ' +
        'gekoppeld, maar er komt geen APK-voorstel of signaal.',
      blokkerend: false,
    })
  }

  return uit
}

/** true zolang er een blokkerende (niet-bevestigde) waarschuwing is. */
export function apkRapportGeblokkeerd(waarschuwingen: readonly ApkRapportWaarschuwing[]): boolean {
  return waarschuwingen.some((w) => w.blokkerend)
}

export type ApkHistorieReden = 'NIEUWERE_KEURING' | 'OUDE_KEURING'

export interface ApkActualiteit {
  /** Nieuwste bekende keuring van het voertuig en recent: raakt de lopende APK-status. */
  actueel: boolean
  /** Er is geen nieuwere keuring vastgelegd (los van de leeftijd). */
  nieuwste: boolean
  /** Waarom het rapport historie is (null = actueel). */
  reden: ApkHistorieReden | null
}

/** Is deze keuring geslaagd (goedgekeurd rapport, of een regel met een nieuwe vervaldatum)? */
function isGeslaagd(k: ApkKeuringMomentDTO): boolean {
  return k.uitslag === 'GOEDGEKEURD' || (k.uitslag === null && k.metVervaldatum)
}

/**
 * Is dit rapport de actuele keuring? Historie (een oud rapport uit de hertypeer-
 * lijst, of een rapport terwijl er al een nieuwere keuring is) wordt vastgelegd
 * maar mag de lopende APK-status niet veranderen: geen afspraak op uitgevoerd,
 * geen afgekeurd-alarm, geen postvak-items sluiten.
 *
 *  - Goedgekeurd: historie zodra er een andere keuring is ná het zelfde-keuring-
 *    venster (3 dagen).
 *  - Afgekeurd: historie zodra er een GESLAAGDE keuring is op dezelfde dag of
 *    later (de herkeuring), of later een andere keuring met een bekende uitslag.
 *    Een latere regel met onbekende uitslag (bv. een factuur) dempt het alarm
 *    NIET: liever een melding te veel dan een afgekeurd voertuig zonder signaal.
 *
 * `andereKeuringen` = de keuring-momenten van het voertuig zonder dit rapport en
 * zonder de regel waar het aan hangt; momenten na vandaag tellen niet.
 */
export function bepaalApkActualiteit(args: {
  keuringsdatum: string
  uitslag: ApkKeuringUitslag
  andereKeuringen: readonly ApkKeuringMomentDTO[]
  /** Vandaag in Europe/Amsterdam (yyyy-mm-dd). */
  vandaag: string
}): ApkActualiteit {
  const k0 = args.keuringsdatum
  const echte = args.andereKeuringen.filter((k) => k.datum <= args.vandaag)
  const nieuwer =
    args.uitslag === 'AFGEKEURD'
      ? echte.some((k) => (k.datum >= k0 && isGeslaagd(k)) || (k.datum > k0 && k.uitslag !== null))
      : echte.some((k) => k.datum > plusDagenYmd(k0, APK_ZELFDE_KEURING_DAGEN))
  if (nieuwer) return { actueel: false, nieuwste: false, reden: 'NIEUWERE_KEURING' }
  if (k0 < plusDagenYmd(args.vandaag, -APK_ACTUEEL_DAGEN)) {
    return { actueel: false, nieuwste: true, reden: 'OUDE_KEURING' }
  }
  return { actueel: true, nieuwste: true, reden: null }
}

/**
 * Wat doet dit rapport met de APK-vervaldatum van het voertuig? Nooit stil
 * schrijven: bij een nieuwere datum komt er een VOORSTEL (status OPEN).
 */
export function bepaalApkVoorstel(args: {
  uitslag: ApkKeuringUitslag
  vervaldatum: string | null
  /** Huidige APK-vervaldatum op het voertuig (yyyy-mm-dd) of null. */
  huidigeApkVervaldatum: string | null
  uitgefaseerd: boolean
  /** Er is al een nieuwere keuring vastgelegd: dit rapport is historie. */
  nieuwereKeuring?: boolean
  /** Vandaag (yyyy-mm-dd): een vervaldatum in het verleden is historie. */
  vandaag?: string
}): ApkVoorstelUitkomst {
  if (args.uitslag !== 'GOEDGEKEURD' || !args.vervaldatum || args.uitgefaseerd) {
    return 'NIET_VAN_TOEPASSING'
  }
  if (args.nieuwereKeuring) return 'HISTORISCH'
  if (args.huidigeApkVervaldatum === args.vervaldatum) return 'AL_ACTUEEL'
  if (args.vandaag && args.vervaldatum < args.vandaag) return 'HISTORISCH'
  if (args.huidigeApkVervaldatum !== null && args.huidigeApkVervaldatum > args.vervaldatum) {
    return 'HISTORISCH'
  }
  return 'OPEN'
}

/** Mensleesbare uitleg bij een voorstel-uitkomst (toast/audit). */
export function apkVoorstelUitleg(
  uitkomst: ApkVoorstelUitkomst,
  vervaldatum: string | null,
  huidigeApkVervaldatum: string | null,
  opts?: { nieuwereKeuring?: boolean; vandaag?: string },
): string | null {
  switch (uitkomst) {
    case 'OPEN':
      return vervaldatum
        ? `APK-voorstel klaargezet: vervaldatum ${apkNlDatum(vervaldatum)} (bevestigen op de Onderhoud-tab van het voertuig).`
        : null
    case 'AL_ACTUEEL':
      return 'De vervaldatum uit het rapport staat al op het voertuig.'
    case 'HISTORISCH':
      // Een nieuwere keuring zegt `apkHistorieUitleg` al (geen tweede zin).
      if (opts?.nieuwereKeuring) return null
      if (opts?.vandaag && vervaldatum && vervaldatum < opts.vandaag) {
        return `De vervaldatum op het rapport (${apkNlDatum(vervaldatum)}) ligt al in het verleden; er is geen voorstel gemaakt.`
      }
      return huidigeApkVervaldatum
        ? `Het voertuig heeft al een latere APK-vervaldatum (${apkNlDatum(huidigeApkVervaldatum)}); er is geen voorstel gemaakt.`
        : null
    default:
      return null
  }
}

/** Uitleg bij een historisch rapport dat de lopende APK-status niet raakt. */
export function apkHistorieUitleg(reden: ApkHistorieReden | null): string | null {
  switch (reden) {
    case 'NIEUWERE_KEURING':
      return 'Er is al een nieuwere APK-keuring van dit voertuig: het rapport is als historie vastgelegd, zonder voorstel, en afspraken, meldingen en het postvak blijven ongewijzigd.'
    case 'OUDE_KEURING':
      return `De keuring is ouder dan ${APK_ACTUEEL_DAGEN} dagen en is als historie vastgelegd: afspraken, meldingen en het postvak blijven ongewijzigd.`
    default:
      return null
  }
}

export type ApkRdwBesluit =
  /** RDW registreert dezelfde datum als het open voorstel: bevestig automatisch. */
  | 'BEVESTIG'
  /** RDW registreert (na de verwerkingsruimte) een andere datum: signaleer. */
  | 'SIGNAAL'
  /** Afwijking, maar RDW krijgt nog even de tijd de keuring te verwerken. */
  | 'WACHT'
  /** Niets te doen. */
  | 'GEEN'

/**
 * RDW-sync tegen een APK-rapport. `vandaag` = NL-kalenderdag van de sync.
 * Een afgewezen voorstel levert geen signaal meer op: de gebruiker heeft het
 * rapport-voorstel zelf al verworpen.
 */
export function beoordeelApkRdwAfstemming(args: {
  rapportVervaldatum: string
  keuringsdatum: string
  voorstelStatus: GarageApkVoorstelStatus | null
  rdwVervaldatum: string | null
  vandaag: string
  verwerkingDagen?: number
}): ApkRdwBesluit {
  if (!args.rdwVervaldatum) return 'GEEN'
  if (args.voorstelStatus === 'AFGEWEZEN') return 'GEEN'
  if (args.rdwVervaldatum === args.rapportVervaldatum) {
    return args.voorstelStatus === 'OPEN' ? 'BEVESTIG' : 'GEEN'
  }
  const ruimte = args.verwerkingDagen ?? APK_RDW_VERWERKING_DAGEN
  return plusDagenYmd(args.keuringsdatum, ruimte) > args.vandaag ? 'WACHT' : 'SIGNAAL'
}
